/**
 * Shared colour/theme preferences for the Trainer/Play table and the Solver grids.
 * Stored per device in localStorage. Pages listen for the change events so the
 * global Settings dialog can update them live from anywhere in the app.
 */

export const TRAINER_APPEARANCE_KEY = "pm_trainer_appearance_v2";
export const SOLVER_COLORS_KEY = "pm_solver_colors_v1";
export const TRAINER_APPEARANCE_EVENT = "pm:trainer-appearance";
export const SOLVER_COLORS_EVENT = "pm:solver-colors";

// ── Trainer / Play table theme ──────────────────────────────────────────────

export const TABLE_STYLE_OPTIONS = {
  racetrack: { label: "Racetrack", radius: "999px" },
  rounded: { label: "Rounded", radius: "44px" },
};
export const FELT_OPTIONS = {
  blue: { label: "Blue", c1: "#2a6fb0", c2: "#1a4f86", c3: "#123a64" },
  emerald: { label: "Emerald", c1: "#14352b", c2: "#0f2a22", c3: "#0a1e18" },
  midnight: { label: "Midnight", c1: "#1c2b33", c2: "#15222a", c3: "#0e181e" },
  royal: { label: "Royal", c1: "#16304d", c2: "#10253d", c3: "#0a1a2c" },
  crimson: { label: "Crimson", c1: "#47201f", c2: "#381817", c3: "#26100f" },
  violet: { label: "Violet", c1: "#33204a", c2: "#28183a", c3: "#1a0f28" },
  charcoal: { label: "Charcoal", c1: "#2e2e2e", c2: "#242424", c3: "#181818" },
};
export const RAIL_OPTIONS = {
  walnut: { label: "Walnut", color: "#23201a" },
  black: { label: "Black", color: "#101010" },
  mahogany: { label: "Mahogany", color: "#3a1f16" },
  navy: { label: "Navy", color: "#141c2c" },
  steel: { label: "Steel", color: "#2b3138" },
};
export const DECK_STYLE_OPTIONS = {
  minimal: { label: "Dark" },
  classic: { label: "Light" },
};
export const DECK_COLORWAYS = {
  four: {
    label: "Four colour",
    dark: { s: "#888888", h: "#c44", d: "#48f", c: "#4a4" },
    light: { s: "#26262b", h: "#c62828", d: "#1565c0", c: "#2e7d32" },
  },
  two: {
    label: "Two colour",
    dark: { s: "#cfcfcf", h: "#e05252", d: "#e05252", c: "#cfcfcf" },
    light: { s: "#26262b", h: "#c62828", d: "#c62828", c: "#26262b" },
  },
};
export const CARD_BACK_OPTIONS = {
  graphite: { label: "Graphite", border: "#333333", tint: "rgba(58,58,58,0.62)", accent: "#5a5a5a" },
  crimson: { label: "Crimson", border: "#5b2320", tint: "rgba(122,32,28,0.55)", accent: "#8f322c" },
  blue: { label: "Blue", border: "#24405e", tint: "rgba(30,70,120,0.55)", accent: "#2f5e94" },
  green: { label: "Green", border: "#1f4a3a", tint: "rgba(22,92,68,0.55)", accent: "#237a5a" },
  purple: { label: "Purple", border: "#3d2a5e", tint: "rgba(76,44,128,0.55)", accent: "#5f3aa0" },
};
export const CARD_BACK_PATTERNS = {
  logo: { label: "Moose logo" },
  stripes: { label: "Stripes" },
  dots: { label: "Dots" },
  weave: { label: "Weave" },
  solid: { label: "Solid" },
};
export const CHIP_COLOR_OPTIONS = {
  gold: { label: "Gold", border: "#B99E4F", a: "#3a2f16", b: "#2a2412", text: "#B99E4F" },
  red: { label: "Red", border: "#d05555", a: "#4a1d1d", b: "#361414", text: "#e08585" },
  blue: { label: "Blue", border: "#5a8fd6", a: "#1c2f4a", b: "#141f36", text: "#8ab4e8" },
  green: { label: "Green", border: "#56ECCF", a: "#15352d", b: "#0e241f", text: "#56ECCF" },
  purple: { label: "Purple", border: "#a678e0", a: "#2f2044", b: "#221632", text: "#c3a1ef" },
  white: { label: "White", border: "#d9d9d9", a: "#3c3c3c", b: "#2b2b2b", text: "#e6e6e6" },
};
export const CHIP_STYLE_OPTIONS = {
  flat: { label: "Flat" },
  striped: { label: "Striped" },
};
export const BACKGROUND_OPTIONS = {
  graphite: { label: "Graphite", color: "#1f1f1f" },
  jet: { label: "Jet", color: "#101012" },
  navy: { label: "Navy", color: "#0f1522" },
  forest: { label: "Forest", color: "#0f1a15" },
  plum: { label: "Plum", color: "#191022" },
};
export const GLOW_OPTIONS = {
  gold: { label: "Gold", color: "#B99E4F" },
  green: { label: "Green", color: "#56ECCF" },
  cyan: { label: "Cyan", color: "#4dc9ff" },
  red: { label: "Red", color: "#e57f7f" },
  purple: { label: "Purple", color: "#b57bff" },
  white: { label: "White", color: "#f0f0f0" },
};
export const DEFAULT_APPEARANCE = {
  tableStyle: "rounded",
  felt: "blue",
  rail: "walnut",
  deckStyle: "minimal",
  deckColors: "four",
  cardBack: "graphite",
  cardBackPattern: "logo",
  chipColor: "gold",
  chipStyle: "flat",
  background: "jet",
  glow: "white",
};
const APPEARANCE_VALIDATORS = {
  tableStyle: TABLE_STYLE_OPTIONS,
  felt: FELT_OPTIONS,
  rail: RAIL_OPTIONS,
  deckStyle: DECK_STYLE_OPTIONS,
  deckColors: DECK_COLORWAYS,
  cardBack: CARD_BACK_OPTIONS,
  cardBackPattern: CARD_BACK_PATTERNS,
  chipColor: CHIP_COLOR_OPTIONS,
  chipStyle: CHIP_STYLE_OPTIONS,
  background: BACKGROUND_OPTIONS,
  glow: GLOW_OPTIONS,
};

export function loadAppearance() {
  let stored = {};
  try {
    stored = JSON.parse(localStorage.getItem(TRAINER_APPEARANCE_KEY) || "{}") || {};
  } catch (_) {}
  const merged = { ...DEFAULT_APPEARANCE };
  Object.keys(APPEARANCE_VALIDATORS).forEach((key) => {
    if (APPEARANCE_VALIDATORS[key][stored[key]]) merged[key] = stored[key];
  });
  return merged;
}

export function saveAppearance(next) {
  try {
    localStorage.setItem(TRAINER_APPEARANCE_KEY, JSON.stringify(next));
  } catch (_) {}
  window.dispatchEvent(new CustomEvent(TRAINER_APPEARANCE_EVENT, { detail: next }));
}

export function hexToRgba(hex, alpha) {
  const h = String(hex || "").replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = parseInt(full, 16);
  if (Number.isNaN(n)) return `rgba(255,255,255,${alpha})`;
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
}

// ── Solver grid colours ─────────────────────────────────────────────────────

export const SOLVER_PALETTE = [
  "#1EAB80", // green (default check/call)
  "#27AE60", // grass
  "#56ECCF", // mint
  "#8A7A01", // olive
  "#C2711D", // orange
  "#C0392B", // bright red
  "#821C2A", // wine (default fold)
  "#8F2C7D", // pink (default bet 3)
  "#C2185B", // magenta
  "#48149E", // purple (default bet 2)
  "#7D3CFF", // violet
  "#077396", // blue (default bet 1)
  "#2E86DE", // sky
  "#5D6D7E", // slate
];
export const SOLVER_DECK_OPTIONS = {
  four: { label: "Four colour", colors: { s: "#888888", h: "#c44", d: "#48f", c: "#4a4" } },
  vivid: { label: "Vivid four", colors: { s: "#d0d0d0", h: "#ff5c5c", d: "#4da3ff", c: "#58d858" } },
  two: { label: "Two colour", colors: { s: "#cfcfcf", h: "#e05252", d: "#e05252", c: "#cfcfcf" } },
};
export const DEFAULT_SOLVER_PREFS = {
  checkCall: "#1EAB80",
  fold: "#821C2A",
  bet1: "#077396",
  bet2: "#48149E",
  bet3: "#8F2C7D",
  deck: "four",
};
const HEX_RE = /^#[0-9a-fA-F]{6}$/;

export function loadSolverPrefs() {
  let stored = {};
  try {
    stored = JSON.parse(localStorage.getItem(SOLVER_COLORS_KEY) || "{}") || {};
  } catch (_) {}
  const merged = { ...DEFAULT_SOLVER_PREFS };
  ["checkCall", "fold", "bet1", "bet2", "bet3"].forEach((key) => {
    if (HEX_RE.test(String(stored[key] || ""))) merged[key] = stored[key];
  });
  if (SOLVER_DECK_OPTIONS[stored.deck]) merged.deck = stored.deck;
  return merged;
}

export function saveSolverPrefs(next) {
  try {
    localStorage.setItem(SOLVER_COLORS_KEY, JSON.stringify(next));
  } catch (_) {}
  window.dispatchEvent(new CustomEvent(SOLVER_COLORS_EVENT, { detail: next }));
}
